import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Terminal, Trash2, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { logger, LogLevel } from '@/utils/logger'
import { cn } from '@/utils/cn'
import { config } from '@/config'

interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  data?: any
  id: string
}

export function LoggerPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [filter, setFilter] = useState<LogLevel | 'ALL'>('ALL')
  const maxLogs = config.logger.maxLogsInPanel

  useEffect(() => {
    // Check if logger panel should be open
    const shouldOpen = localStorage.getItem('loggerPanelOpen') === 'true'
    setIsOpen(shouldOpen)
  }, [])

  useEffect(() => {
    if (!isOpen) return

    // Intercept console methods to capture logs
    const originalMethods = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
    }

    const captureLog = (level: LogLevel, args: any[]) => {
      const timestamp = new Date().toISOString()
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2)
          } catch {
            return String(arg)
          }
        }
        return String(arg)
      }).join(' ')

      const logEntry: LogEntry = {
        timestamp,
        level,
        message,
        data: args.length > 1 ? args.slice(1) : undefined,
        id: `${timestamp}-${Math.random()}`
      }

      setTimeout(() => {
        setLogs(prev => {
          const newLogs = [logEntry, ...prev]
          return newLogs.slice(0, maxLogs)
        })
      }, 0)
    }

    console.log = (...args) => {
      originalMethods.log(...args)
      if (args[0]?.includes?.('[INFO]')) {
        captureLog(LogLevel.INFO, args)
      } else if (args[0]?.includes?.('[DEBUG]')) {
        captureLog(LogLevel.DEBUG, args)
      } else {
        captureLog(LogLevel.INFO, args)
      }
    }

    console.info = (...args) => {
      originalMethods.info(...args)
      captureLog(LogLevel.INFO, args)
    }

    console.warn = (...args) => {
      originalMethods.warn(...args)
      captureLog(LogLevel.WARN, args)
    }

    console.error = (...args) => {
      originalMethods.error(...args)
      captureLog(LogLevel.ERROR, args)
    }

    console.debug = (...args) => {
      originalMethods.debug(...args)
      captureLog(LogLevel.DEBUG, args)
    }

    return () => {
      // Restore original methods
      Object.assign(console, originalMethods)
    }
  }, [isOpen, maxLogs])

  const togglePanel = () => {
    const newState = !isOpen
    setIsOpen(newState)
    localStorage.setItem('loggerPanelOpen', String(newState))
  }

  const clearLogs = () => {
    setLogs([])
  }

  const downloadLogs = () => {
    const logText = logs.map(log => 
      `${log.timestamp} [${log.level}] ${log.message}`
    ).join('\n')
    
    const blob = new Blob([logText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `logs-${new Date().toISOString()}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filteredLogs = filter === 'ALL' 
    ? logs 
    : logs.filter(log => log.level === filter)

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case LogLevel.DEBUG: return 'text-gray-400'
      case LogLevel.INFO: return 'text-blue-400'
      case LogLevel.WARN: return 'text-yellow-400'
      case LogLevel.ERROR: return 'text-red-400'
      default: return 'text-gray-300'
    }
  }

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={togglePanel}
        className={cn(
          "fixed bottom-16 right-4 z-50 p-3 rounded-full shadow-lg",
          "bg-dark-800 border border-dark-700 hover:bg-dark-700",
          "transition-colors duration-200",
          isOpen && "bg-brand-blue hover:bg-brand-blue-dark"
        )}
        title="Toggle Logger Panel"
      >
        <Terminal className="h-5 w-5" />
      </motion.button>

      {/* Logger Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-32 right-4 z-40 w-96 h-96 bg-dark-900 border border-dark-700 rounded-lg shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-dark-700 bg-dark-800">
              <h3 className="text-sm font-medium">Logger Output</h3>
              <div className="flex items-center gap-2">
                {/* Filter Dropdown */}
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as LogLevel | 'ALL')}
                  className="text-xs bg-dark-700 border border-dark-600 rounded px-2 py-1"
                >
                  <option value="ALL">All</option>
                  <option value={LogLevel.DEBUG}>Debug</option>
                  <option value={LogLevel.INFO}>Info</option>
                  <option value={LogLevel.WARN}>Warn</option>
                  <option value={LogLevel.ERROR}>Error</option>
                </select>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={downloadLogs}
                  className="h-7 w-7 p-0"
                  title="Download logs"
                >
                  <Download className="h-3 w-3" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearLogs}
                  className="h-7 w-7 p-0"
                  title="Clear logs"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={togglePanel}
                  className="h-7 w-7 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Logs */}
            <div className="h-[calc(100%-48px)] overflow-y-auto p-3 font-mono text-xs">
              {filteredLogs.length === 0 ? (
                <div className="text-center text-gray-500 mt-8">
                  No logs to display
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredLogs.map((log) => (
                    <div
                      key={log.id}
                      className={cn(
                        "py-1 px-2 rounded bg-dark-800/50 break-all",
                        "hover:bg-dark-800 transition-colors"
                      )}
                    >
                      <span className="text-gray-500">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      {' '}
                      <span className={cn("font-medium", getLevelColor(log.level))}>
                        [{log.level}]
                      </span>
                      {' '}
                      <span className="text-gray-300">
                        {log.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-dark-800 border-t border-dark-700 px-3 flex items-center justify-between text-xs text-gray-500">
              <span>{filteredLogs.length} logs</span>
              <span>Logger: {logger.isEnabled() ? 'Enabled' : 'Disabled'}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}